import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Eye, EyeOff } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { notify } from '../utils/notifications';
import PasswordRequirements from '../components/PasswordRequirements';
import PolicyConfirmations from '../components/PolicyConfirmations';
import type { PolicyConfirmationsValue } from '../services/authService';
import { passwordPolicyError } from '../utils/passwordPolicy';
import RubikooBrand from '../components/RubikooBrand';

export default function SignUpScreen() {
  const navigate = useNavigate();
  const { signUp } = useApp();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmations, setConfirmations] = useState<PolicyConfirmationsValue>({
    adultAttested: false,
    termsAccepted: false,
  });

  const handleSignUp = async () => {
    if (!name.trim()) { notify.error('Please enter your name'); return; }
    if (!email.trim()) { notify.error('Please enter your email'); return; }
    const passwordError = passwordPolicyError(password);
    if (passwordError) { notify.error(passwordError); return; }
    if (!confirmations.adultAttested || !confirmations.termsAccepted) {
      notify.error('Please confirm your age and accept the Terms of Service');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await signUp(
        email.trim(),
        password,
        name.trim(),
        confirmations,
      );
      if (error) {
        if (error.message.toLowerCase().includes('already registered') || error.message.toLowerCase().includes('already exists')) {
          notify.error('Email already in use', 'Try signing in instead');
        } else {
          notify.error('Sign up failed', error.message);
        }
        return;
      }
      notify.success('Account created!', 'Please check your email to verify your account.');
      navigate('/verify-email', { replace: true });
    } catch (err) {
      notify.error('Connection error', 'Could not reach the server — check your internet or try again');
      console.error('Sign up error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="rubikoo-auth-page size-full flex flex-col overflow-y-auto transition-colors duration-300"
      style={{ backgroundColor: 'var(--app-bg)' }}
    >
      {/* Header */}
      <div className="flex flex-col items-center justify-center pt-8 pb-8 px-6">
        <RubikooBrand />
        <h1 className="mt-8 text-[32px] font-semibold tracking-tight text-center" style={{ color: 'var(--app-text)' }}>Create account</h1>
        <p className="text-base text-center mt-2" style={{ color: 'var(--app-text-muted)' }}>One account for your event experiences.</p>
      </div>

      {/* Form */}
      <div className="flex flex-col gap-4 px-6 flex-1">
        <div className="flex flex-col gap-2">
          <label htmlFor="signup-name" className="text-base" style={{ color: 'var(--app-text)' }}>Full Name</label>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Your full name"
            className="rounded-lg px-4 text-base outline-none transition-colors w-full"
            style={{
              backgroundColor: 'var(--app-input-bg)',
              border: '1px solid var(--app-input-border)',
              color: 'var(--app-text)',
              height: '52px',
            }}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="signup-email" className="text-base" style={{ color: 'var(--app-text)' }}>Email Address</label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="name@example.com"
            className="rounded-lg px-4 text-base outline-none transition-colors w-full"
            style={{
              backgroundColor: 'var(--app-input-bg)',
              border: '1px solid var(--app-input-border)',
              color: 'var(--app-text)',
              height: '52px',
            }}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="signup-password" className="text-base" style={{ color: 'var(--app-text)' }}>Password</label>
          <div
            className="flex items-stretch rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--app-input-bg)',
              border: '1px solid var(--app-input-border)',
            }}
          >
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Create a password"
              className="min-w-0 flex-1 bg-transparent px-4 text-base outline-none"
              style={{ color: 'var(--app-text)', height: '56px' }}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword(!showPassword)}
              className="px-4 transition-opacity active:opacity-70"
              style={{ color: 'var(--app-text-subtle)' }}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <PasswordRequirements password={password} />
        </div>

        <PolicyConfirmations
          value={confirmations}
          onChange={setConfirmations}
          disabled={isLoading}
        />

        <div className="pt-4">
          <button
            onClick={handleSignUp}
            disabled={isLoading || !confirmations.adultAttested || !confirmations.termsAccepted}
            className="h-14 rounded-xl text-white text-lg w-full flex items-center justify-center active:scale-95 transition-transform disabled:opacity-70"
            style={{
              backgroundColor: 'var(--app-accent-solid)',
              boxShadow: '0 10px 20px var(--app-accent-glow)',
            }}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              'Create Account'
            )}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-center gap-1 pb-8 pt-6 px-6">
        <span className="text-base" style={{ color: 'var(--app-text-muted)' }}>Already have an account?</span>
        <button
          onClick={() => navigate('/signin')}
          className="text-base active:opacity-70 transition-opacity"
          style={{ color: 'var(--app-accent)', textDecoration: 'underline', textDecorationColor: 'var(--app-accent-border)' }}
        >
          Sign in
        </button>
      </div>
    </div>
  );
}
